// client/src/coms/components/ServerBanner.js
// A server's banner, the same everywhere it appears (public discovery cards, the server header, Server Settings):
// the owner's uploaded image (coms_servers.banner_url), or a generated fallback whose colour and faint topographic
// contours are derived from the server key, so a server always looks the same.
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { comsApiGet } from '../comsApi';
import './ServerBanner.css';

// ---------- fallback: persistent colour + contours ----------
const BANNER_HUES = [152, 168, 186, 200, 214, 228, 248, 28];

export function bannerHue(seed) {
  let h = 0;
  for (const ch of String(seed || '')) h = (h * 31 + ch.codePointAt(0)) % 360;
  return BANNER_HUES[h % BANNER_HUES.length];
}

function seededRandom(text) {
  let h = 2166136261;
  for (const ch of String(text || '')) h = Math.imul(h ^ ch.codePointAt(0), 16777619);
  return () => {
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

// Rings around a few seeded summits spread along the width, wobbled by seeded sine terms.
export function contourPaths(seed, width = 320, height = 110) {
  const rnd = seededRandom(seed || 'voxemera');
  const peaks = Math.max(2, Math.round(width / 260));
  const segment = width / peaks;
  const paths = [];
  for (let p = 0; p < peaks; p++) {
    const cx = p === 0 ? 40 + rnd() * 90 : p * segment + 30 + rnd() * 100;
    const cy = height * (0.18 + rnd() * 0.64);
    const terms = [2, 3, 5].map((k) => ({ k, a: 0.08 + rnd() * 0.12, p: rnd() * Math.PI * 2 }));
    const rings = 7 + Math.floor(rnd() * 3);
    const step = 11 + rnd() * 5;
    for (let i = 1; i <= rings; i++) {
      const r = i * step;
      let d = '';
      for (let j = 0; j <= 48; j++) {
        const t = (j / 48) * Math.PI * 2;
        const wobble = terms.reduce((acc, w) => acc + w.a * Math.sin(w.k * t + w.p + i * 0.35), 0);
        const x = cx + Math.cos(t) * r * 1.35 * (1 + wobble);
        const y = cy + Math.sin(t) * r * (1 + wobble);
        d += `${j ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
      }
      paths.push(d + 'Z');
    }
  }
  return paths;
}

function safeBannerUrl(raw) {
  const s = String(raw || '').trim();
  return s && (/^https:\/\//i.test(s) || s.startsWith('/')) ? s : '';
}

// Fills its (position: relative) parent. shape 'card' (about 3:1) or 'wide' (header strips).
export function ServerBannerArt({ seed, banner, shape = 'card', className = '' }) {
  const url = safeBannerUrl(banner);
  const [w, h] = shape === 'wide' ? [1400, 90] : [320, 110];
  const paths = useMemo(() => (url ? [] : contourPaths(seed, w, h)), [url, seed, w, h]);
  const style = url ? { backgroundImage: `url("${url.replace(/"/g, '%22')}")` } : { '--vx-sb-hue': bannerHue(seed) };
  return (
    <span className={`vx-sb-art ${url ? 'has-image' : ''} ${className}`} style={style} aria-hidden="true">
      {url ? null : (
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid slice" focusable="false">
          {paths.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </svg>
      )}
    </span>
  );
}

// ---------- display settings (Server Settings → Server identity) ----------
// Where the banner shows inside the server: one place only, or nowhere. The public discovery card is separate and
// always shows the banner.
export const BANNER_POSITIONS = [
  { id: 'top', label: 'Top of server, beneath navigation' },
  { id: 'sidebar', label: 'Above channel list, inside sidebar' },
  { id: 'overview', label: 'Server overview only' },
];
export const BANNER_SIZES = [
  { id: 'small', label: 'Small' },
  { id: 'medium', label: 'Medium' },
  { id: 'large', label: 'Large' },
];
// Off inside the server until the owner turns it on; public cards and pages outside the server always show it.
export const DEFAULT_BANNER_DISPLAY = { visible: false, position: 'top', size: 'medium' };

export function normaliseBannerDisplay(d) {
  const v = d && typeof d === 'object' ? d : {};
  return {
    visible: typeof v.visible === 'boolean' ? v.visible : DEFAULT_BANNER_DISPLAY.visible,
    position: BANNER_POSITIONS.some((p) => p.id === v.position) ? v.position : DEFAULT_BANNER_DISPLAY.position,
    size: BANNER_SIZES.some((s) => s.id === v.size) ? v.size : DEFAULT_BANNER_DISPLAY.size,
  };
}

// True when the banner belongs at this position inside the server.
export function bannerShowsAt(display, position) {
  const d = normaliseBannerDisplay(display);
  return d.visible && d.position === position;
}

// ---------- shared branding cache (logo + banner + display per server key) ----------
const cache = new Map(); // key -> { logo, banner, display }
const fetchedAt = new Map(); // key -> ms of the last server read
const pending = new Map();
const listeners = new Set();
// Members pick up an owner's changes when they come back to a server after this long (and on every reload).
const REFRESH_MS = 60 * 1000;

function notify() {
  for (const fn of listeners) fn();
}

export function publishServerBranding(key, patch) {
  const k = String(key || '').trim();
  if (!k) return;
  const prev = cache.get(k) || { logo: '', banner: '', display: DEFAULT_BANNER_DISPLAY };
  const next = { ...prev, ...patch };
  if (patch && patch.display) next.display = normaliseBannerDisplay(patch.display);
  cache.set(k, next);
  notify();
}

function loadBranding(k) {
  if (pending.has(k)) return;
  if (cache.has(k) && Date.now() - (fetchedAt.get(k) || 0) < REFRESH_MS) return;
  const p = comsApiGet(`/server-logo/${encodeURIComponent(k)}`)
    .then((r) => {
      fetchedAt.set(k, Date.now());
      publishServerBranding(k, {
        logo: String((r && r.logo_url) || ''),
        banner: String((r && r.banner_url) || ''),
        display: normaliseBannerDisplay(r && r.banner_display),
      });
    })
    .catch(() => {
      if (!cache.has(k)) publishServerBranding(k, { logo: '', banner: '', display: DEFAULT_BANNER_DISPLAY });
    })
    .finally(() => pending.delete(k));
  pending.set(k, p);
}

// { logo, banner, display, loaded } for a server the viewer is a member of.
export function useServerBranding(serverKey) {
  const k = String(serverKey || '').trim();
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    if (k) loadBranding(k);
    return () => listeners.delete(fn);
  }, [k]);
  const v = k ? cache.get(k) : null;
  return { logo: v ? v.logo : '', banner: v ? v.banner : '', display: v ? v.display : DEFAULT_BANNER_DISPLAY, loaded: !!v };
}

function initialsOf(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : String(name || '?').slice(0, 2)).toUpperCase();
}

export function ServerBannerLogo({ logo, name, className = '' }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [logo]);
  return (
    <span className={`vx-sb-logo ${className}`} aria-hidden="true">
      {logo && !broken ? <img src={logo} alt="" onError={() => setBroken(true)} /> : <span>{initialsOf(name)}</span>}
    </span>
  );
}

// "Top of server, beneath navigation": the strip at the top of a server's main view (banner, logo and name).
// Purely informational: it never covers or replaces navigation. Renders nothing unless the owner chose this
// position, so the compact layout is untouched otherwise. preview: { logo, banner, display } for Server Settings.
export function ServerHeaderBanner({ serverKey, name, preview = null }) {
  const live = useServerBranding(preview ? '' : serverKey);
  const b = preview || live;
  if (!preview && (!live.loaded || !bannerShowsAt(live.display, 'top'))) return null;
  const { size } = normaliseBannerDisplay(b.display);
  return (
    <div className={`vx-shb is-${size}${preview ? ' is-preview' : ''}`} data-testid="server-header-banner" data-size={size}>
      <ServerBannerArt seed={serverKey} banner={b.banner} shape="wide" />
      <span className="vx-shb-scrim" aria-hidden="true" />
      <ServerBannerLogo logo={b.logo} name={name} />
      <span className="vx-shb-name">{name}</span>
    </div>
  );
}

// "Above channel list, inside sidebar": the banner art above the server's logo and name in the channel sidebar.
// compact: the phone channel list, where it is kept shallower.
export function ServerSidebarBanner({ serverKey, preview = null, compact = false }) {
  const live = useServerBranding(preview ? '' : serverKey);
  const b = preview || live;
  if (!preview && (!live.loaded || !bannerShowsAt(live.display, 'sidebar'))) return null;
  const { size } = normaliseBannerDisplay(b.display);
  return (
    <div className={`vx-ssb is-${size}${compact ? ' is-compact' : ''}${preview ? ' is-preview' : ''}`} data-testid="server-sidebar-banner" data-size={size}>
      <ServerBannerArt seed={serverKey} banner={b.banner} shape="card" />
    </div>
  );
}

// The server overview card (opened from the server name in the sidebar). The banner heads it only when the owner
// chose "Server overview only".
export function ServerOverviewCard({ serverKey, name, logo, description = '', preview = null, children = null }) {
  const live = useServerBranding(preview ? '' : serverKey);
  const b = preview || live;
  const d = normaliseBannerDisplay(b.display);
  const withBanner = preview ? true : live.loaded && bannerShowsAt(live.display, 'overview');
  return (
    <div className={`vx-sov${withBanner ? ` has-banner is-${d.size}` : ''}${preview ? ' is-preview' : ''}`} data-testid="server-overview-card">
      {withBanner ? (
        <div className="vx-sov-banner" data-testid="server-overview-banner" data-size={d.size}>
          <ServerBannerArt seed={serverKey} banner={b.banner} shape="card" />
        </div>
      ) : null}
      <div className="vx-sov-head">
        <ServerBannerLogo logo={logo !== undefined ? logo : b.logo} name={name} />
        <span className="vx-sov-name">{name}</span>
      </div>
      {description ? <p className="vx-sov-desc">{description}</p> : null}
      {children}
    </div>
  );
}

export function ServerOverviewDialog({ serverKey, name, logo, onClose, children = null }) {
  const [description, setDescription] = useState('');
  useEffect(() => {
    let alive = true;
    if (serverKey) {
      comsApiGet(`/servers/${encodeURIComponent(serverKey)}/profile`)
        .then((r) => alive && setDescription(String((r && r.description) || '')))
        .catch(() => {});
    }
    return () => {
      alive = false;
    };
  }, [serverKey]);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const body = (
    <div className="vx-sov-backdrop" onMouseDown={onClose} data-testid="server-overview-dialog">
      <div className="vx-sov-dialog" role="dialog" aria-modal="true" aria-label={`${name || 'Server'} overview`} onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="vx-sov-close" onClick={onClose} aria-label="Close server overview" title="Close">
          ✕
        </button>
        <ServerOverviewCard serverKey={serverKey} name={name} logo={logo} description={description}>
          {children}
        </ServerOverviewCard>
      </div>
    </div>
  );
  return typeof document !== 'undefined' && document.body ? createPortal(body, document.body) : body;
}
